import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Form } from '@/components/ui/form'
import ProtectedContent from '../../../../components/ProtectedContent'
import { FormEntityContext } from '../../../../config/context/forms'
import { useTeam } from '../../../../config/context/team'
import { useAccessRestrictions } from '../../../../config/queries'
import { useGameVersion } from '../../../../hooks/useGameVersion'
import { updateTeam } from '../../../../api/team'
import { displayErrorToast } from '../../../../helpers'
import AccessRestrictionTable from '../../../TeamDashboard/subpages/TeamSettings/components/AccessSectionTable'
import {
  accessRestrictionSchema,
  EditableAccessRestriction,
  validateAccessRestrictions,
} from '../../../TeamDashboard/subpages/TeamSettings/components/accessRestrictionSchema'
import StepLayout from '../StepLayout'
import StepFooter from '../StepFooter'
import { StepProps } from '../types'

const formSchema = z
  .object({
    access_restrictions_attributes: z.array(accessRestrictionSchema),
  })
  .superRefine(validateAccessRestrictions)

type AccessFormValues = z.infer<typeof formSchema>

interface AccessStepFormProps extends StepProps {
  restrictions: EditableAccessRestriction[]
}

const AccessStepForm = ({
  goNext,
  goBack,
  isFirst,
  restrictions,
}: AccessStepFormProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const gameVersion = useGameVersion()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<AccessFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      access_restrictions_attributes:
        restrictions as AccessFormValues['access_restrictions_attributes'],
    },
  })

  const onSubmit = (values: AccessFormValues) => {
    setSubmitting(true)

    updateTeam(team.id, {
      access_restrictions_attributes: values.access_restrictions_attributes,
    } as unknown as Partial<wowaudit.Team>)
      .then((updatedTeam) => {
        queryClient.setQueryData(['team', { path: team.path }], updatedTeam)
        queryClient.invalidateQueries({
          queryKey: ['accessRestrictions', { id: team.id }],
        })
        setSubmitting(false)
        goNext()
      })
      .catch((error) => {
        displayErrorToast(error, t)
        setSubmitting(false)
      })
  }

  return (
    <StepLayout
      icon={ShieldCheck}
      title='Control team access'
      description={`Decide who can see and manage your team. Access is granted based on the highest matching rule, and ${
        gameVersion.has('blizzard_api')
          ? 'your in-game guild master always keeps admin access.'
          : 'the user who created the guild has admin access by default.'
      }`}
    >
      <Form {...form}>
        <FormEntityContext.Provider
          value={{ entityType: 'team', i18nPrefix: 'forms.fields' }}
        >
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <p className='mb-4'>
              Section-specific rules can be added, and will override base rules
              for that section.
            </p>

            <ProtectedContent section='base' requirement='admin'>
              <AccessRestrictionTable scope='team' />
            </ProtectedContent>

            <StepFooter
              showBack={!isFirst}
              onBack={goBack}
              onContinue={form.handleSubmit(onSubmit)}
              continueLoading={submitting}
              onSkip={goNext}
            />
          </form>
        </FormEntityContext.Provider>
      </Form>
    </StepLayout>
  )
}

const AccessStep = (props: StepProps) => {
  const { team } = useTeam()
  const { data: restrictions, isPending } = useAccessRestrictions(team.id)

  if (isPending) return null

  return <AccessStepForm {...props} restrictions={restrictions ?? []} />
}

export default AccessStep
